import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import { 
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid-pro';
import IconButton from '@mui/material/IconButton';
import { Edit } from '@mui/icons-material';
import { NotificationRuleListFragmentProps } from './NotificationRuleListFragment.props';
import { CustomDataGrid } from '../../../../components/layouts/CustomDataGrid';
import { DropdownItem, SelectMenu } from '../../../../components/atoms/SelectMenu';
import { NotificationConfigType, GridFeatureModeConstant } from '../../../../configs/enums';
import { NotificationConfigObject } from '../../../../@types';
import { NotificationRulesConfigModelFragment } from '../NotificationRuleConfigModelFragment/NotificationRuleConfigModelFragment';
import { getDataGridListColumnNames } from '../../../../utils/array-utils';
import { getNotificationListColumnLabel } from '../../../../utils/notification';
import { useGetNotificationConfigsByCateogry } from '../../../../queries/notification-config-query';
import { NOTIFICATION_EVENTS, NOTIFICATION_MESSAGE_TYPES, NOTIFICATION_CONTACT_RECORD_TYPES } from '../../../../constants/notification.constant';
import { GridContainer } from '../../../../styled/common.styles';
import { ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY } from '../../../../constants/common';
import { serviceColumnSelectionItems, shipmentColumnSelectionItems } from '../../../../constants/notificationRule';

export const NotificationRuleListFragment: React.FC<NotificationRuleListFragmentProps> = ({
  modelData,
  refetchData,
  editPermission
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCountState, setRowCountState] = useState(0);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [selectedNotificationType, setSelectedNotificationType] = useState<string>('-1');
  const [notificationConfigData, setNotificationConfigData] = useState<NotificationConfigObject[]>([]);
  const [editConfigData, setEditConfigData] = useState<NotificationConfigObject>();
  const [openModel, setOpenModel] = useState(false);

  const { data: notificationConfig, refetch: refetchConfigData, isLoading, isRefetching } = useGetNotificationConfigsByCateogry(selectedNotificationType, { filterModel, page, pageSize, sortModel });

  useEffect(() => {
    selectedNotificationType !== '-1' && refetchConfigData();
  }, [selectedNotificationType, filterModel, page, pageSize, sortModel, refetchData]);

  useEffect(() => {
    setNotificationConfigData(notificationConfig?.data || []);
    setRowCountState(notificationConfig?.total || 0);
  }, [notificationConfig]);

  const changeType = (type: string) => {
    setNotificationConfigData([]);
    setSelectedNotificationType(type);
  };

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    setFilterModel({ ...filterModel });
  }, []);
  
  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(sortModel);
  }, []);

  const handleSelectionModelChange = useCallback((selectionModel: GridRowSelectionModel) => {
    setSelectionModel(selectionModel);
  }, []);

  const closeModel = () => {
    refetchConfigData();
    setOpenModel(false);
  };

  const notificationTypes: Array<DropdownItem> = [
    { value: NotificationConfigType.Service, label: NotificationConfigType.Service },
    { value: NotificationConfigType.Shipment, label: NotificationConfigType.Shipment }
  ];

  useEffect(() => {
    if(selectedNotificationType === NotificationConfigType.Service) {
      setColumns([
        {
          field: 'id',
          headerName: 'ID',
          width: 60,
          type: 'number',
          valueFormatter: (value: number) => value?.toString()
        },
        {
          field: 'name',
          headerName: 'Name',
          width: 150,
          valueGetter: (_value, row) => row.name,
        },
        {
          field: 'clientcontract',
          headerName: 'Client/Contract',
          width: 175,
          valueGetter: (_value, row) => row.clientContractHierarchy,
        },
        {
          field: 'serviceType',
          headerName: 'Service Type',
          width: 120,
          valueGetter: (_value, row) => getDataGridListColumnNames(modelData?.serviceActionProcessTypes?.serviceTypes, row, 'serviceType')
        },
        {
          field: 'actionType',
          headerName: 'Action Type',
          width: 100,
          valueGetter: (_value, row) => getDataGridListColumnNames(modelData?.serviceActionProcessTypes?.actionTypes, row, 'actionType')
        },
        {
          field: 'processType',
          headerName: 'Process Type',
          width: 120,
          valueGetter: (_value, row) => getDataGridListColumnNames(modelData?.serviceActionProcessTypes?.processTypes, row, 'processType')
        },
        {
          field: 'priority',
          headerName: 'Priority',
          width: 100,
          valueGetter: (_value, row) => row.priorityCodes
        },
        {
          field: 'region',
          headerName: 'Region',
          width: 100,
          valueGetter: (_value, row) => getDataGridListColumnNames(modelData?.regionTypes, row, 'region')
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 100,
          valueGetter: (_value, row) => getDataGridListColumnNames(modelData?.serviceStatuses, row, 'status')
        },
        {
          field: 'event',
          headerName: 'Event',
          width: 150,
          valueGetter: (_value, row) => getDataGridListColumnNames(NOTIFICATION_EVENTS, row, 'event')
        },
        {
          field: 'messageType',
          headerName: 'Message Type',
          width: 120,
          valueGetter: (_value, row) => getNotificationListColumnLabel(NOTIFICATION_MESSAGE_TYPES, row.messageType),
        },
    
        {
          field: 'contactRecordType',
          headerName: 'Contact Record Type',
          width: 175,
          valueGetter: (_value, row) => getNotificationListColumnLabel(NOTIFICATION_CONTACT_RECORD_TYPES, row.contactRecordType),
        },
        {
          field: 'template',
          headerName: 'Template',
          width: 150,
          valueGetter: (_value, row) => row.templateName,
        },
        {
          field: 'emailAddresses',
          headerName: 'Email Addresses',
          width: 220,
          valueGetter: (_value, row) => row.emailAddresses,
        },
        {
          field: 'phoneNumbers',
          headerName: 'Phone Numbers',
          width: 150,
          valueGetter: (_value, row) => row.phoneNumbers,
        },
        {
          field: 'considerTradingHours',
          headerName: 'Consider Traing Hours',
          width: 175,
          type: 'singleSelect',
          valueOptions: ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY,
          valueFormatter: (_value, row) => row.considerTradingHours ? 'Yes' : 'No',
        },
        {
          field: 'addServiceLog',
          headerName: 'Add Service Log',
          width: 150,
          type: 'singleSelect',
          valueOptions: ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY,
          valueFormatter: (_value, row) => row.addServiceLog ? 'Yes' : 'No',
        },
        {
          field: 'isActive',
          headerName: 'Is Active',
          width: 150,
          type: 'singleSelect',
          valueOptions: ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY,
          valueFormatter: (_value, row) => row.isActive ? 'Yes' : 'No',
        },
        {
          field: 'action',
          headerName: '',
          sortable: false,
          filterable: false,
          width: 40,
          align: 'center',
          renderCell: (params: GridRenderCellParams) => (
            <Grid>
              {
                editPermission && <IconButton aria-label="edit" onClick={() => {
                  setEditConfigData(params.row);
                  setOpenModel(true);
                }}>
                  <Edit />
                </IconButton>
              }
            </Grid>
          )
        }
      ]);
    }
    else {
      setColumns([
        {
          field: 'id',
          headerName: 'ID',
          width: 60,
          type: 'number',
          valueFormatter: (value: number) => value?.toString()
        },
        {
          field: 'name',
          headerName: 'Name',
          width: 150,
          valueGetter: (_value, row) => row.name,
        },
        {
          field: 'clientcontract',
          headerName: 'Client/Contract',
          width: 175,
          valueGetter: (_value, row) => row.clientContractHierarchy,
        },
        {
          field: 'shipmentType',
          headerName: 'Shipment Type',
          width: 120,
          valueGetter: (_value, row) => getDataGridListColumnNames(modelData?.shipmentTypes, row, 'shipmentType')
        },
        {
          field: 'courier',
          headerName: 'Courier',
          width: 100,
          valueGetter: (_value, row) => getDataGridListColumnNames(modelData?.couriers, row, 'courier')
        },
        {
          field: 'shipmentStatus',
          headerName: 'Shipment Status',
          width: 150,
          valueGetter: (_value, row) => getDataGridListColumnNames(modelData?.shipmentStatuses, row, 'shipmentStatus')
        },
        {
          field: 'event',
          headerName: 'Event',
          width: 150,
          valueGetter: (_value, row) => getDataGridListColumnNames(NOTIFICATION_EVENTS, row, 'event') 
        },
        {
          field: 'messageType',
          headerName: 'Message Type',
          width: 120,
          valueGetter: (_value, row) => getNotificationListColumnLabel(NOTIFICATION_MESSAGE_TYPES, row.messageType),
        },
    
        {
          field: 'contactRecordType',
          headerName: 'Contact Record Type',
          width: 175,
          valueGetter: (_value, row) => getNotificationListColumnLabel(NOTIFICATION_CONTACT_RECORD_TYPES, row.contactRecordType),
        },
        {
          field: 'template',
          headerName: 'Template',
          width: 150,
          valueGetter: (_value, row) => row.templateName,
        },
        {
          field: 'emailAddresses',
          headerName: 'Email Addresses',
          width: 220,
          valueGetter: (_value, row) => row.emailAddresses,
        },
        {
          field: 'phoneNumbers',
          headerName: 'Phone Numbers',
          width: 150,
          valueGetter: (_value, row) => row.phoneNumbers,
        },
        {
          field: 'considerTradingHours',
          headerName: 'Consider Traing Hours',
          width: 175,
          type: 'singleSelect',
          valueOptions: ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY,
          valueFormatter: (_value, row) => row.considerTradingHours ? 'Yes' : 'No',
        },
        {
          field: 'addServiceLog',
          headerName: 'Add Service Log',
          width: 150,
          type: 'singleSelect',
          valueOptions: ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY,
          valueFormatter: (_value, row) => row.addServiceLog ? 'Yes' : 'No',
        },
        {
          field: 'isActive',
          headerName: 'Is Active',
          width: 150,
          type: 'singleSelect',
          valueOptions: ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY,
          valueFormatter: (_value, row) => row.isActive ? 'Yes' : 'No',
        },
        {
          field: 'action',
          headerName: '',
          sortable: false,
          filterable: false,
          width: 40,
          align: 'center',
          renderCell: (params: GridRenderCellParams) => (<Grid>
            <IconButton aria-label="edit" onClick={() => {
              setEditConfigData(params.row);
              setOpenModel(true);
            }}>
              <Edit />
            </IconButton>
          </Grid>)
        }
      ]);
    }
  }, [selectedNotificationType, notificationConfig]);

  return (
    <Grid container>
      <Grid mb={2} width="150px">
        <SelectMenu
          id="noti-type"
          label="Type"
          required={true}
          items={notificationTypes}
          selectedValue={selectedNotificationType}
          onChange={changeType}
          optionalLabelEnabled={true}
        />
      </Grid>
      <GridContainer>
        <CustomDataGrid
          id="notification-rule-list"
          key={selectedNotificationType}
          columns={columns}
          rows={notificationConfigData || []}
          rowCount={rowCountState}
          exportFileName=""
          enableCSVExport={false}
          columnSelectionItems={selectedNotificationType === NotificationConfigType.Service ? serviceColumnSelectionItems : shipmentColumnSelectionItems}
          filterMode={GridFeatureModeConstant.server}
          onFilterModelChange={(filterModel: GridFilterModel) => {
            onFilterChange(filterModel);
          }}
          loading={selectedNotificationType !== '-1' && (isLoading || isRefetching)}
          rowsPerPageOptions={[25, 50, 100]}
          pagination={true}
          page={page}
          pageSize={pageSize}
          paginationMode={GridFeatureModeConstant.server}
          onPageChange={(newPage) => {
            setPage(newPage);
          }}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
          }}
          sortingMode={GridFeatureModeConstant.server}
          onSortModelChange={handleSortModelChange}
          selectionModel={selectionModel}
          onSelectionModelChange={handleSelectionModelChange}
          componentsProps={{
            toolbar: {
              printOptions: { disableToolbarButton: false }
            }
          }}
          filterModel={filterModel}
          getRowHeight={() => 'auto'}
        />
      </GridContainer>
      <NotificationRulesConfigModelFragment open={openModel} closeModel={closeModel} modelData={modelData} data={editConfigData} />
    </Grid>
  );
};